import React, { useState } from 'react';
import { FileText, BookOpen, HelpCircle, MessageSquare, Copy, Check, Printer, AlertTriangle, ExternalLink, Calendar as CalendarIcon } from 'lucide-react';

const NoteRHConges = ({ authUser, onOpenNoteService }) => {
  const [activeTab, setActiveTab] = useState('note');
  const [copied, setCopied] = useState(false);

  const isManagerOrAbove = ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const tabs = [
    { id: 'note', label: 'Note Interne', icon: FileText },
    { id: 'memo', label: 'Mémo 10 Points', icon: BookOpen },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    ...(isManagerOrAbove ? [{ id: 'manager', label: 'Message Manager', icon: MessageSquare }] : [])
  ];

  const managerMessage = `Rappel congés payés — Best Events (Convention HCR)

Bonjour à tous,

Petit rappel sur les règles de congés payés :

- Vous acquérez 2,5 jours ouvrables par mois travaillé (du lundi au samedi).
- La période de référence va du 1er juin au 31 mai.
- Les demandes se font via l'application Staff Manager.
- Une demande déposée n'est PAS automatiquement acceptée. Attendez la validation avant de prendre vos dispositions.
- Anticipez vos demandes au maximum, surtout en période événementielle.
- En cas de plusieurs demandes sur les mêmes dates, la priorité est définie selon l'ancienneté, la situation familiale et les congés déjà pris.

Pour toute question, rapprochez-vous de votre responsable ou du service RH.

Merci de votre coopération.
La Direction — Best Events`;

  return (
    <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="bg-indigo-100 dark:bg-indigo-900/40 p-2 sm:p-3 rounded-xl">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-base sm:text-xl font-bold text-text-primary">Congés Payés — HCR</h2>
            <p className="text-xs sm:text-sm text-text-muted">Documentation interne Best Events</p>
          </div>
        </div>
        <button
          onClick={handlePrint}
          className="px-3 py-2 bg-elevated-1 text-text-secondary rounded-lg hover:bg-elevated-1 text-xs sm:text-sm font-medium transition flex items-center justify-center gap-2"
        >
          <Printer className="w-4 h-4" />
          Imprimer
        </button>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 mb-4 sm:mb-6 bg-elevated-1 rounded-lg p-1 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-surface text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Contenu des onglets */}
      <div className="print:block">

        {/* ONGLET 1 — Note Interne */}
        {activeTab === 'note' && (
          <div className="prose prose-sm max-w-none">
            <div className="border-b pb-4 mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-1">Note interne — Politique des congés payés</h3>
              <p className="text-sm text-text-muted">Convention Collective HCR (IDCC 1979) — Best Events, traiteur événementiel</p>
            </div>

            <section className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                Période de référence
              </h4>
              <div className="bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 dark:border-blue-500 p-4 rounded-r-lg">
                <p className="text-sm text-text-secondary">
                  Les congés payés sont calculés sur une <strong>période de référence allant du 1er juin au 31 mai</strong> de l'année suivante.
                  C'est durant cette période que vous acquérez vos droits à congés. La prise effective des congés se fait ensuite sur la période suivante,
                  selon les besoins de l'activité et la validation de votre direction.
                </p>
              </div>
            </section>

            <section className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                Acquisition des congés
              </h4>
              <div className="bg-elevated-1 rounded-lg p-4 space-y-2 text-sm text-text-secondary">
                <p>Chaque salarié acquiert <strong>2,5 jours ouvrables par mois de travail effectif</strong>, soit <strong>30 jours ouvrables par an</strong> (5 semaines).</p>
                <p>Les <strong>jours ouvrables</strong> sont les jours du <strong>lundi au samedi inclus</strong>. Le dimanche n'est pas compté.</p>
                <p>Un mois au cours duquel vous avez travaillé au moins 10 jours (ou équivalent) est compté comme un mois complet pour l'acquisition.</p>
              </div>
            </section>

            <section className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
                <span className="bg-purple-100 text-purple-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                Bonus d'ancienneté (Convention HCR)
              </h4>
              <div className="bg-purple-50 dark:bg-purple-950/40 rounded-lg p-4 text-sm text-text-secondary">
                <p className="mb-3">La convention HCR prévoit des <strong>jours supplémentaires</strong> selon votre ancienneté dans l'entreprise :</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1">Ancienneté</th>
                      <th className="text-center py-1">Jours supplémentaires / an</th>
                    </tr>
                  </thead>
                  <tbody className="text-text-secondary">
                    <tr className="border-b border-border-subtle"><td className="py-1">Moins de 1 an</td><td className="text-center">0</td></tr>
                    <tr className="border-b border-border-subtle"><td className="py-1">1 à 4 ans</td><td className="text-center">1</td></tr>
                    <tr className="border-b border-border-subtle"><td className="py-1">5 à 9 ans</td><td className="text-center">2</td></tr>
                    <tr className="border-b border-border-subtle"><td className="py-1">10 à 14 ans</td><td className="text-center">3</td></tr>
                    <tr className="border-b border-border-subtle"><td className="py-1">15 à 19 ans</td><td className="text-center">4</td></tr>
                    <tr className="border-b border-border-subtle"><td className="py-1">20 à 24 ans</td><td className="text-center">5</td></tr>
                    <tr><td className="py-1">25 ans et plus</td><td className="text-center">6</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
                <span className="bg-amber-100 text-amber-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                Procédure de demande
              </h4>
              <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-4 space-y-2 text-sm text-text-secondary">
                <p><strong>1.</strong> Déposez votre demande via <strong>l'application Staff Manager</strong> (module RH &gt; Congés).</p>
                <p><strong>2.</strong> Précisez les dates de début et de fin, le type de congé et le motif si nécessaire.</p>
                <p><strong>3.</strong> Votre demande est transmise à la direction pour validation.</p>
                <p><strong>4.</strong> Vous recevrez une réponse (acceptation ou refus avec motif).</p>
                <p><strong>5.</strong> Une fois acceptée, les jours apparaissent automatiquement sur votre planning.</p>
              </div>
            </section>

            {/* ================================================================ */}
            {/* SECTION ALIGNÉE SUR LA NOTE DE SERVICE NS-2026-01 (référence officielle) */}
            {/* ================================================================ */}
            <section className="mb-6">
              <div className="bg-slate-50 border-2 border-slate-300 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-slate-700 text-white p-2 rounded-lg flex-shrink-0">
                    <FileText className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-text-primary text-base">Règles applicables Best Events SAS — Note de service NS-2026-01</h4>
                    <p className="text-xs text-text-muted mt-0.5">Période 1er mai 2026 → 30 avril 2027 — Document opposable</p>
                  </div>
                </div>

                <div className="space-y-3 text-sm text-text-secondary">
                  <div className="bg-surface rounded-md p-3 border border-slate-200">
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5 mb-1">
                      <CalendarIcon className="w-4 h-4" aria-hidden="true" />
                      Période de prise des congés payés
                    </p>
                    <p>Du <strong>1er mai au 30 avril</strong> de l'année suivante. Cette période est fixée par l'Entreprise en application de l'article L3141-15 du Code du travail.</p>
                  </div>

                  <div className="bg-surface rounded-md p-3 border border-slate-200">
                    <p className="font-semibold text-slate-700 mb-2">Calendrier obligatoire des 5 semaines de congés payés annuels</p>
                    <ul className="list-disc list-inside space-y-1 ml-1">
                      <li><strong>1 semaine</strong> à prendre obligatoirement entre le <strong>1er et le 30 novembre</strong></li>
                      <li><strong>1 semaine</strong> à prendre obligatoirement entre le <strong>1er juillet et le 31 août</strong> (date arrêtée par la Direction selon le calendrier des prestations)</li>
                      <li><strong>3 semaines</strong> à prendre entre le <strong>1er janvier et le 30 avril</strong></li>
                    </ul>
                  </div>

                  <div className="bg-surface rounded-md p-3 border border-slate-200">
                    <p className="font-semibold text-slate-700 mb-1">Règle d'alternance avec le binôme</p>
                    <p>Pour garantir la continuité de service, les congés sont posés en <strong>alternance stricte</strong> avec le binôme désigné. Deux salariés d'un même binôme <strong>ne peuvent jamais</strong> être absents simultanément sur une même semaine de congés. La liste des binômes est communiquée en début d'année.</p>
                  </div>

                  <div className="bg-surface rounded-md p-3 border border-slate-200">
                    <p className="font-semibold text-slate-700 mb-2">Délais limitatifs de dépôt des demandes</p>
                    <ul className="list-disc list-inside space-y-1 ml-1">
                      <li>Semaine de novembre : <strong>avant le 30 septembre</strong> de l'année en cours</li>
                      <li>Semaine de juillet/août : <strong>avant le 31 mars</strong> de l'année en cours</li>
                      <li>3 semaines de janvier-avril : <strong>avant le 30 novembre</strong> de l'année précédente</li>
                    </ul>
                    <p className="mt-2 text-xs text-text-secondary">La Direction répond au moins un (1) mois avant la date de départ envisagée (art. D3141-6 C. trav.).</p>
                  </div>

                  <div className="bg-amber-50 dark:bg-amber-950/40 border-l-4 border-amber-400 p-3 rounded-r-md">
                    <p className="flex items-start gap-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>L'<strong>ordre des départs</strong> est affiché sur staff.best-events.fr au plus tard le <strong>31 mars</strong> de chaque année pour la période du 1er mai au 30 avril suivant.</span>
                    </p>
                  </div>

                  {onOpenNoteService && (
                    <button
                      onClick={onOpenNoteService}
                      className="w-full sm:w-auto mt-2 inline-flex items-center gap-2 px-4 py-2 min-h-[44px] bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition"
                      aria-label="Consulter la note de service NS-2026-01 complète"
                    >
                      <ExternalLink className="w-4 h-4" aria-hidden="true" />
                      Consulter la note de service complète (NS-2026-01)
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
                <span className="bg-red-100 text-red-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">5</span>
                Règles essentielles
              </h4>
              <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-400 dark:border-red-600 p-4 rounded-r-lg space-y-2 text-sm text-text-secondary">
                <p><strong>Une demande de congé n'est PAS une autorisation.</strong> Tant que vous n'avez pas reçu la validation, ne considérez pas vos dates comme acquises.</p>
                <p>L'employeur organise les départs en congé selon les <strong>nécessités de l'activité</strong>, notamment en période événementielle.</p>
                <p>En cas de demandes simultanées sur les mêmes dates, la priorité tient compte de l'ancienneté, de la situation familiale (enfants scolarisés) et des congés déjà pris dans l'année.</p>
                <p>Partir en congé sans validation préalable constitue une <strong>absence injustifiée</strong> pouvant entraîner des sanctions disciplinaires.</p>
              </div>
            </section>

            <section className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
                <span className="bg-green-100 text-green-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">6</span>
                Spécificités Best Events
              </h4>
              <div className="bg-green-50 dark:bg-green-950/40 rounded-lg p-4 space-y-2 text-sm text-text-secondary">
                <p>En tant que traiteur événementiel, notre activité connaît des <strong>pics saisonniers</strong> (mariages, fêtes de fin d'année, événements d'entreprise). Pendant ces périodes, les possibilités de congés sont limitées.</p>
                <p>Les <strong>Extras</strong> ne bénéficient pas de congés payés via l'application (rémunérés au forfait par événement).</p>
                <p>Les <strong>saisonniers et CDD</strong> acquièrent des congés au prorata de leur temps de présence. En fin de contrat, les jours non pris sont indemnisés (indemnité compensatrice de congés payés).</p>
              </div>
            </section>

            <div className="bg-elevated-1 rounded-lg p-4 text-xs text-text-muted mt-6">
              <p className="font-semibold mb-1">Base juridique de référence</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Code du travail : articles L.3141-1 à L.3141-33 (congés payés)</li>
                <li>Convention collective HCR (IDCC 1979) : articles relatifs aux congés payés et à l'ancienneté</li>
                <li>Les règles internes de Best Events complètent ces dispositions</li>
              </ul>
            </div>
          </div>
        )}

        {/* ONGLET 2 — Mémo 10 Points */}
        {activeTab === 'memo' && (
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-text-primary mb-4">Mémo Congés Payés — 10 points essentiels</h3>

            {[
              { num: 1, title: 'Période de référence', desc: 'Du 1er juin au 31 mai de l\'année suivante. C\'est sur cette période que vos droits sont calculés.' },
              { num: 2, title: 'Acquisition', desc: 'Vous acquérez 2,5 jours ouvrables de congé par mois de travail effectif.' },
              { num: 3, title: 'Total annuel', desc: '30 jours ouvrables par an, soit 5 semaines de congés payés.' },
              { num: 4, title: 'Jours ouvrables', desc: 'Du lundi au samedi inclus (6 jours par semaine). Le dimanche ne compte pas.' },
              { num: 5, title: 'Bonus ancienneté', desc: 'La convention HCR accorde jusqu\'à 6 jours supplémentaires selon votre ancienneté dans l\'entreprise.' },
              { num: 6, title: 'Décompte simple', desc: '1 jour de congé posé = 1 jour ouvrable décompté de votre solde. Pas de conversion en heures.' },
              { num: 7, title: 'Types de congés', desc: 'CP (congés payés) et RTT consomment votre solde. Maladie et sans solde n\'impactent pas votre compteur CP.' },
              { num: 8, title: 'Demande obligatoire', desc: 'Toute demande se fait via l\'application Staff Manager. Attendez la validation avant de considérer vos dates comme acquises.' },
              { num: 9, title: 'Planning automatique', desc: 'Dès que vos congés sont acceptés, ils apparaissent automatiquement sur votre planning.' },
              { num: 10, title: 'Export mensuel', desc: 'Chaque mois, un tableau récapitulatif combine vos heures travaillées et vos jours de congés pour la préparation de la paie.' }
            ].map(item => (
              <div key={item.num} className="flex gap-3 items-start p-3 rounded-lg hover:bg-elevated-1 transition">
                <span className="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {item.num}
                </span>
                <div>
                  <p className="font-semibold text-text-primary text-sm">{item.title}</p>
                  <p className="text-sm text-text-secondary mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ONGLET 3 — FAQ */}
        {activeTab === 'faq' && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-text-primary mb-4">Foire aux questions — Congés payés HCR</h3>

            {[
              { q: 'Combien de jours de congés j\'acquiers par mois ?', a: '2,5 jours ouvrables par mois de travail effectif, soit 30 jours ouvrables (5 semaines) par an. Si vous avez de l\'ancienneté, la convention HCR prévoit des jours supplémentaires.' },
              { q: 'Qu\'est-ce qu\'un jour ouvrable ?', a: 'Un jour ouvrable va du lundi au samedi inclus. Seul le dimanche est exclu du décompte. Quand vous posez une semaine complète de congé (lundi au samedi), 6 jours ouvrables sont décomptés.' },
              { q: 'Est-ce que je peux poser mes congés quand je veux ?', a: 'Non. Vous formulez une demande, mais c\'est l\'employeur qui valide les dates en fonction des besoins de l\'activité. En période événementielle (mariages, fêtes), les possibilités sont plus limitées.' },
              { q: 'Combien de temps à l\'avance dois-je faire ma demande ?', a: 'Le plus tôt possible. En pratique, prévoyez au minimum 3 à 4 semaines d\'avance, voire plus en période de forte activité. Plus vous anticipez, plus vous avez de chances d\'obtenir les dates souhaitées.' },
              { q: 'Mon responsable peut-il refuser mes dates ?', a: 'Oui. L\'employeur organise les départs en congé selon les nécessités du service. Il doit motiver son refus et vous proposer des dates alternatives si possible.' },
              { q: 'Que se passe-t-il si plusieurs personnes veulent partir en même temps ?', a: 'La priorité est définie selon des critères objectifs : ancienneté, situation de famille (enfants scolarisés, conjoint avec contraintes), congés déjà pris dans l\'année.' },
              { q: 'Est-ce qu\'une demande déposée est automatiquement acceptée ?', a: 'Non, jamais. Une demande reste "en attente" tant qu\'elle n\'a pas été explicitement validée. Ne prenez aucune disposition (billets, réservations) avant d\'avoir reçu la confirmation.' },
              { q: 'Que se passe-t-il si je pars sans validation ?', a: 'C\'est considéré comme une absence injustifiée. Cela peut entraîner des sanctions disciplinaires pouvant aller jusqu\'au licenciement.' },
              { q: 'Comment fonctionne le décompte pour un saisonnier ou un CDD ?', a: 'Les droits à congés sont acquis au prorata du temps de présence (2,5 jours ouvrables par mois travaillé). En fin de contrat, les jours non pris donnent lieu à une indemnité compensatrice de congés payés.' },
              { q: 'Que deviennent les jours de congé non pris ?', a: 'En principe, les congés non pris à la fin de la période de référence suivante sont perdus, sauf accord de report avec l\'employeur. Certains cas (maladie, maternité) peuvent justifier un report.' },
              { q: 'C\'est quoi le bonus d\'ancienneté HCR ?', a: 'La convention HCR accorde des jours de congés supplémentaires selon votre ancienneté : 1 jour après 1 an, 2 jours après 5 ans, 3 après 10 ans, 4 après 15 ans, 5 après 20 ans, et 6 après 25 ans.' },
              { q: 'Les Extras ont-ils droit aux congés payés ?', a: 'Les Extras sont rémunérés au forfait par événement et ne bénéficient pas de congés payés via l\'application. Leur rémunération intègre une indemnité compensatrice de congés payés (10%).' },
              { q: 'Où puis-je consulter mon solde de congés ?', a: 'Dans l\'application Staff Manager, rendez-vous dans la section RH > Solde CP. Vous y trouverez vos jours acquis, pris et restants pour la période en cours.' },
              { q: 'Les jours de maladie consomment-ils mon solde de congés ?', a: 'Non. Les arrêts maladie et les congés sans solde ne sont pas décomptés de votre solde de congés payés. Seuls les CP et les RTT impactent votre compteur.' }
            ].map((item, i) => (
              <details key={i} className="group border border-border-subtle rounded-lg">
                <summary className="flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-elevated-1 transition">
                  <span className="font-medium text-sm text-text-primary">{item.q}</span>
                  <span className="text-text-muted group-open:rotate-180 transition-transform">
                    <ChevronDown className="w-4 h-4" />
                  </span>
                </summary>
                <div className="px-4 pb-4 text-sm text-text-secondary border-t border-border-subtle pt-3">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        )}

        {/* ONGLET 4 — Message Manager */}
        {activeTab === 'manager' && isManagerOrAbove && (
          <div>
            <h3 className="text-lg font-bold text-text-primary mb-4">Message prêt à envoyer</h3>
            <p className="text-sm text-text-muted mb-4">
              Copiez ce message et envoyez-le sur WhatsApp, par email ou dans votre groupe interne.
            </p>

            <div className="relative">
              <div className="bg-elevated-1 border border-border-subtle rounded-lg p-5 text-sm text-text-secondary whitespace-pre-line leading-relaxed">
                {managerMessage}
              </div>
              <button
                onClick={() => handleCopy(managerMessage)}
                className={`absolute top-3 right-3 px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                  copied
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                    : 'bg-surface border border-border-strong text-text-secondary hover:bg-elevated-1'
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copié !' : 'Copier'}
              </button>
            </div>

            <div className="mt-4 bg-amber-50 dark:bg-amber-950/40 border-l-4 border-amber-400 p-3 rounded-r-lg">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                <strong>Conseil :</strong> Envoyez ce rappel en début de saison événementielle et avant chaque période de forte activité.
                Adaptez le message si nécessaire avec les dates spécifiques de votre planning.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant ChevronDown utilisé dans la FAQ
const ChevronDown = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m6 9 6 6 6-6"/>
  </svg>
);

export default NoteRHConges;
